/**
 * 친구 페이지
 * 친구 목록, 요청 보기/수락, 삭제
 */
import { apiFriendList, apiFriendRequests, apiFriendRequest, apiFriendAccept, apiFriendRemove, apiFriendChat } from '../lib/api';
import { escapeHtml, getAvatarGradient } from '../lib/utils';
import { toast } from '../lib/toast';
import type { AppStateManager } from '../app';

let _friendsApp: AppStateManager;

export function renderFriendsPage(container: HTMLElement, app: AppStateManager): void {
  _friendsApp = app;
  loadFriends();
}

async function loadFriends(): Promise<void> {
  const el = document.getElementById('friends-content');
  if (!el) return;
  el.innerHTML = '<div class="flex items-center justify-center py-20"><div class="animate-spin rounded-full h-8 w-8 border-2 border-kalpha-400 border-t-transparent"></div></div>';

  try {
    const [flRes, frRes] = await Promise.all([apiFriendList(), apiFriendRequests()]);
    const friends = flRes.success ? flRes.data || [] : [];
    const requests = frRes.success ? frRes.data || [] : [];

    el.innerHTML = `
      <!-- 친구 추가 -->
      <div class="flex gap-2 mb-5">
        <input id="friend-add-input" type="text" placeholder="친구 아이디 입력" class="input-field flex-1">
        <button id="btn-friend-add" class="btn btn-primary px-4 py-2.5 text-sm shrink-0 flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
          추가
        </button>
      </div>

      <!-- 받은 요청 -->
      ${requests.length > 0 ? `
      <div class="section-header mb-2">받은 요청 (${requests.length})</div>
      <div class="space-y-1 mb-5">
        ${requests.map(r => `
        <div class="flex items-center gap-3 px-3 py-2.5 surface-secondary rounded-xl card">
          <div class="w-10 h-10 ${getAvatarGradient(r.display_name)} rounded-full flex items-center justify-center shrink-0"><span class="text-white font-bold text-sm">${r.display_name.charAt(0)}</span></div>
          <div class="flex-1 min-w-0"><p class="text-sm font-semibold text-primary">${escapeHtml(r.display_name)}</p><p class="text-xs text-tertiary">@${escapeHtml(r.username)}</p></div>
          <button class="btn-accept px-3 py-1.5 text-xs rounded-lg font-semibold text-white shrink-0" style="background:var(--text-green)" data-username="${escapeHtml(r.username)}">수락</button>
        </div>`).join('')}
      </div>` : ''}

      <!-- 친구 목록 -->
      <div class="section-header mb-2">친구 (${friends.length})</div>
      ${friends.length === 0 ? '<p class="empty-state text-sm text-center py-8">아직 친구가 없습니다</p>' : ''}
      <div class="space-y-1">
        ${friends.map(f => `
        <div class="flex items-center gap-3 px-3 py-2.5 surface-secondary rounded-xl card">
          <div class="w-10 h-10 ${getAvatarGradient(f.display_name)} rounded-full flex items-center justify-center shrink-0"><span class="text-white font-bold text-sm">${f.display_name.charAt(0)}</span></div>
          <div class="flex-1 min-w-0"><p class="text-sm font-semibold text-primary">${escapeHtml(f.display_name)}</p><p class="text-xs text-tertiary">@${escapeHtml(f.username)}</p></div>
          <button class="btn-chat px-3 py-1.5 text-xs rounded-lg font-semibold text-white shrink-0" style="background:var(--text-green)" data-username="${escapeHtml(f.username)}">채팅</button>
          <button class="btn-remove px-3 py-1.5 text-xs rounded-lg font-semibold text-white shrink-0" style="background:var(--bg-toast-error)" data-username="${escapeHtml(f.username)}">삭제</button>
        </div>`).join('')}
      </div>
    `;

    // 이벤트 바인딩
    const addBtn = document.getElementById('btn-friend-add') as HTMLButtonElement;
    const addInput = document.getElementById('friend-add-input') as HTMLInputElement;

    async function doFriendAdd() {
      const u = addInput.value.trim();
      if (!u) return;
      addBtn.disabled = true; addBtn.textContent = '추가 중...';
      try {
        const res = await apiFriendRequest(u);
        if (res.success) { toast(res.message || '요청 전송', 'success'); addInput.value = ''; }
        else toast(res.error || '실패', 'error');
      } finally { addBtn.disabled = false; addBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg> 추가'; }
    }
    addBtn?.addEventListener('click', doFriendAdd);
    addInput?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); doFriendAdd(); } });

    el.querySelectorAll('.btn-accept').forEach(btn => {
      btn.addEventListener('click', async function(this: HTMLButtonElement) {
        this.disabled = true; this.textContent = '처리 중...';
        const res = await apiFriendAccept(this.dataset.username!);
        if (res.success) { toast('수락됨', 'success'); loadFriends(); }
        else { toast(res.error || '실패', 'error'); this.disabled = false; this.textContent = '수락'; }
      });
    });

    el.querySelectorAll('.btn-remove').forEach(btn => {
      btn.addEventListener('click', async function(this: HTMLButtonElement) {
        if (!confirm('친구를 삭제하시겠습니까?')) return;
        this.disabled = true; this.textContent = '삭제 중...';
        const res = await apiFriendRemove(this.dataset.username!);
        if (res.success) { toast('삭제됨', 'success'); loadFriends(); }
        else { toast(res.error || '실패', 'error'); this.disabled = false; this.textContent = '삭제'; }
      });
    });

    el.querySelectorAll('.btn-chat').forEach(btn => {
      btn.addEventListener('click', async function(this: HTMLButtonElement) {
        this.disabled = true; this.textContent = '열기...';
        const res = await apiFriendChat(this.dataset.username!);
        if (res.success && res.data) {
          _friendsApp.navigateTo('chat', { roomId: res.data.room_id });
        } else { toast(res.error || '실패', 'error'); this.disabled = false; this.textContent = '채팅'; }
      });
    });

  } catch { el.innerHTML = '<p class="empty-state text-sm text-center py-20">불러오기 실패</p>'; }
}
